/**
 * The Editing board's actions in the new console (prototype v108 §2.3; Traci's review). Pure: the booking and what the
 * editor entered in, the booking update and the side effects out. They make the same writes as the current Editing
 * dashboard's buttons (photo_/video_ review columns, the legacy review_* columns, the lifecycle stage), so both
 * consoles agree while both are in use.
 *
 *   approve   Initial Review or Issues → Approved & In Progress (stage Media Approved; Billing is told to pay talent)
 *   issue     Initial Review or Approved → Issues, with a note (stage Media Issue)
 *   note      add a note to an item already in Issues
 *   finish    Approved → Ready to Deliver, with the talent's internal rating 1–5 (board.js planReadyToDeliver)
 *   deliver   Ready to Deliver → delivered with the gallery link; the client is emailed; when every media type on the
 *             booking is delivered the booking moves to Media Delivered (Full). With `partial` (Traci's doc, Oct 2026:
 *             part of the media now, the rest still to come): the client gets the partial-delivery email, the booking
 *             moves to Media Delivered (Partial) and the item goes back to Approved & In Progress for the rest
 *
 * Admin's exceptions for one talent with no completion form (planTalentException; Traci, 29 Oct 2026) are separate.
 */
import { planReadyToDeliver, receivedStatus, reviewStatus, talentRoles, mediaGroup } from './board.js'

export const RATINGS = [
  { value: 5, label: '5 - Excellent' },
  { value: 4, label: '4 - Good' },
  { value: 3, label: '3 - Acceptable' },
  { value: 2, label: '2 - Needs Improvement' },
  { value: 1, label: '1 - Poor' }
]

const bad = (status, error, message) => ({ ok: false, status, error, message })
const isCancelled = b => String(b?.booking_status || '').toLowerCase().startsWith('cancel')
const MEDIA_LABEL = { photo: 'Photo', video: 'Video' }

/** The note line written to booking_notes for an issue (the notepad reads these back). */
export const issueNotePrefix = media => `Media issue (${MEDIA_LABEL[media] || media}): `

/** Is a link something a client can open? */
export function isGalleryLink(link) {
  try {
    const u = new URL(String(link || '').trim())
    return u.protocol === 'https:' || u.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * @param {object} booking  bookings row: id, booking_status, review_status, photo_/video_review_status, videographer_id
 * @param {object} input    { action, media: 'photo'|'video', note?, rating?, link? }
 * @param {object} ctx      { actorId, now }
 * @returns {{ ok: true, update: object, stage: string|null, changeType: string|null, note: string|null, notifyBilling: boolean, emailClient: boolean, rating: number|null }
 *          | { ok: false, status: number, error: string, message: string }}
 */
export function planEditingAction(booking, input = {}, { actorId = null, now = new Date().toISOString(), received = false } = {}) {
  const media = input.media
  if (media !== 'photo' && media !== 'video') return bad(400, 'INVALID', 'Media must be photo or video')
  if (!booking) return bad(404, 'NOT_FOUND', 'Booking not found')
  if (isCancelled(booking)) return bad(409, 'CANCELLED', 'This booking is cancelled')
  // received: the talent's completion form is in, though the booking's state never opened (board.js receivedStatus)
  const status = receivedStatus(reviewStatus(booking, media), received ? { submitted_at: true } : null)
  const note = String(input.note || '').trim()
  const base = { stage: null, changeType: null, note: null, notifyBilling: false, emailClient: false, rating: null }

  switch (input.action) {
    case 'approve': {
      if (!['pending_review', 'has_issues'].includes(status)) return bad(409, 'WRONG_STATE', 'Only media in Initial Review or Issues can be approved')
      return {
        ok: true, ...base,
        update: { [`${media}_review_status`]: 'approved', [`${media}_reviewed_at`]: now, review_status: 'approved', reviewed_at: now, reviewed_by: actorId },
        stage: 'media_approved', changeType: 'media_review_approved', notifyBilling: true
      }
    }
    case 'issue': {
      if (!['pending_review', 'approved'].includes(status)) return bad(409, 'WRONG_STATE', 'Only media in Initial Review or Approved & In Progress can go to Issues')
      if (note.length < 3) return bad(400, 'INVALID', 'Describe the issue')
      return {
        ok: true, ...base,
        update: { [`${media}_review_status`]: 'has_issues', [`${media}_reviewed_at`]: now, [`${media}_review_issues`]: note.slice(0, 2000), review_status: 'has_issues', reviewed_at: now, reviewed_by: actorId, review_issues: note.slice(0, 2000) },
        stage: 'media_issue', changeType: 'media_review_issues', note: issueNotePrefix(media) + note.slice(0, 2000)
      }
    }
    case 'note': {
      if (status !== 'has_issues') return bad(409, 'WRONG_STATE', 'Notes are added to media in Issues')
      if (note.length < 3) return bad(400, 'INVALID', 'Write the note')
      return { ok: true, ...base, update: { [`${media}_review_issues`]: note.slice(0, 2000) }, note: issueNotePrefix(media) + note.slice(0, 2000) }
    }
    case 'finish': {
      const rating = Number(input.rating)
      if (!Number.isInteger(rating) || rating < 1 || rating > 5) return bad(400, 'INVALID', 'Pick an internal rating from 1 to 5')
      const plan = planReadyToDeliver(booking, media, now)
      if (!plan.ok) return plan
      return { ok: true, ...base, update: plan.update, rating }
    }
    case 'deliver': {
      if (status !== 'ready_for_delivery') return bad(409, 'WRONG_STATE', 'Only media in Ready to Deliver can be delivered')
      const link = String(input.link || '').trim()
      if (!isGalleryLink(link)) return bad(400, 'INVALID', 'Enter the gallery link (https://…)')
      if (input.partial === true) {
        return {
          ok: true, ...base,
          update: { [`${media}_review_status`]: 'approved', [`${media}_gallery_link`]: link },
          stage: 'media_delivered_partial', changeType: 'media_delivered_partial', emailClient: true, partial: true, link,
          note: `Partial ${MEDIA_LABEL[media].toLowerCase()} delivery sent to the client: ${link}. The rest is still to come; back in Approved & In Progress.`
        }
      }
      const other = media === 'photo' ? 'video' : 'photo'
      const otherDone = other === 'video'
        ? !booking.videographer_id || booking.video_review_status === 'delivered'
        : booking.photo_review_status === 'delivered' || Boolean(booking.photo_delivered_at)
      const update = { [`${media}_review_status`]: 'delivered', [`${media}_delivered_at`]: now, [`${media}_gallery_link`]: link }
      if (otherDone) Object.assign(update, { review_status: 'delivered', media_delivered_at: now })
      return { ok: true, ...base, update, stage: otherDone ? 'media_delivered_full' : null, changeType: otherDone ? 'media_delivered' : null, emailClient: true, partial: false, link }
    }
    default:
      return bad(400, 'INVALID', 'Unknown action')
  }
}

const ROLE_NAME = { photographer: 'Photographer', second_photographer: 'Second Photographer', videographer: 'Videographer', second_videographer: 'Second Videographer' }
const newestFirst = (x, y) => String(y.submitted_at || '').localeCompare(String(x.submitted_at || ''))

/**
 * Admin only (the route checks), for one talent with no completion form (Traci, 29 Oct 2026). Whether the talent is
 * paid follows whether they delivered media that was approved, not whether the form is in:
 *   without_media  the talent never delivered. The job goes ahead without them (editing_jobs.missing_roles); they are
 *                  not paid, so Billing is not told and the Pay Talent card leaves them out
 *   without_form   the talent delivered (the Box link) but never sent the form. Admin approves their media: a
 *                  completion form is recorded for them, already approved, and Billing is told to pay them as usual
 * Either way the reason goes in the booking notes, and the photo or video job moves to Approved & In Progress once
 * every other talent on it is approved (board.js mediaGroup). One talent's exception never decides another's pay.
 * @param {object} booking  bookings row with the talent ids, review columns and _media_url / _uploaded_at
 * @param {object} input    { kind, role, reason, link? }
 * @param {object} ctx      { forms: the booking's job_completion_forms, job: the media's editing_jobs row, names, actorId, now, today }
 */
export function planTalentException(booking, input = {}, { forms = [], job = null, names = new Map(), actorId = null, now = new Date().toISOString(), today } = {}) {
  if (!booking) return bad(404, 'NOT_FOUND', 'Booking not found')
  if (isCancelled(booking)) return bad(409, 'CANCELLED', 'This booking is cancelled')
  const kind = input.kind
  if (kind !== 'without_media' && kind !== 'without_form') return bad(400, 'INVALID', 'Unknown exception')
  const t = talentRoles(booking).find(x => x.role === input.role)
  if (!t) return bad(400, 'INVALID', 'No talent is assigned to this role')
  const media = t.media
  const reason = String(input.reason || '').trim().slice(0, 1500)
  if (reason.length < 3) return bad(400, 'INVALID', kind === 'without_media' ? 'Say why the job goes ahead without this talent' : 'Say why the media is approved without the form')
  const link = String(input.link || '').trim()
  if (kind === 'without_form' && !isGalleryLink(link)) return bad(400, 'INVALID', 'Enter the link to their media (https://…)')
  const roleOf = f => f.talent_role || 'photographer'
  const own = forms.filter(f => roleOf(f) === t.role).sort(newestFirst)[0] || null
  if (own?.submitted_at) return bad(409, 'FORM_IN', 'This talent sent their completion form: approve it as usual')
  const proceeded = job?.proceeded_at ? job.missing_roles || [] : []
  if (kind === 'without_media' && proceeded.includes(t.role)) return bad(409, 'ALREADY', 'The job already goes ahead without this talent')

  const label = MEDIA_LABEL[media]
  const who = `${names.get(t.talent_id) || ROLE_NAME[t.role]} (${ROLE_NAME[t.role]})`
  const form = kind === 'without_form'
    ? { booking_id: booking.id, talent_role: t.role, photographer_id: t.talent_id, total_files: 0, submitted_at: now, [media === 'video' ? 'videos_upload_link' : 'photos_upload_link']: link, wedding_notes: `Entered by Admin without a completion form: ${reason}`, review_status: 'approved', reviewed_at: now, reviewed_by: actorId }
    : null
  const jobPatch = kind === 'without_media'
    ? { booking_id: booking.id, media, proceeded_at: job?.proceeded_at || now, proceeded_by: actorId, proceed_reason: reason.slice(0, 500), missing_roles: [...new Set([...proceeded, t.role])] }
    : null

  // Ready to edit once every talent on the media is approved or gone without
  const after = form ? [...forms.filter(f => roleOf(f) !== t.role), form] : forms
  const formsBy = new Map(after.map(f => [`${booking.id}:${roleOf(f)}`, f]))
  const g = mediaGroup(booking, media, formsBy, { today, job: jobPatch ? { ...job, ...jobPatch } : job })
  const open = !['approved', 'ready_for_delivery', 'delivered'].includes(reviewStatus(booking, media))
  const plan = g.ready && open ? planEditingAction({ ...booking, [`${media}_review_status`]: 'pending_review' }, { action: 'approve', media }, { actorId, now }) : null
  const update = plan?.ok
    ? { ...plan.update, ...(link && !booking[`${media}_media_url`] ? { [`${media}_media_url`]: link } : {}), ...(link && !booking[`${media}_uploaded_at`] ? { [`${media}_uploaded_at`]: now } : {}) }
    : {}
  const moved = Boolean(plan?.ok)
  // Traci's Loom (28 Sep 2026): approvals move the booking's pipeline too. A talent's media approved without the form
  // while another on the media is still out puts a booking still at or before Event Day on Media Uploaded (one in);
  // once every talent is in, the approve above moves it on as usual.
  const EARLY = ['booked', 'logistics_completed', 'client_talent_communication_in_progress', 'talent_5_day_check_in', 'event_day']
  const partial = !moved && kind === 'without_form' && (!booking.lifecycle_stage || EARLY.includes(booking.lifecycle_stage)) ? 'media_uploaded' : null
  const note = kind === 'without_media'
    ? `${label}: going ahead without ${who}, who sent no media; not paid for this booking. Reason: ${reason}`
    : `${label}: ${who} sent their media without a completion form. Approved without the form; Billing told to pay them. Media: ${link}. Reason: ${reason}`
  return {
    ok: true, kind, media, role: t.role, talent_id: t.talent_id, form, form_id: own?.id || null, job: jobPatch,
    update, stage: moved ? plan.stage : partial, moved, notifyBilling: kind === 'without_form', note,
    audit: `${label}: ${kind === 'without_media' ? `went ahead without ${who}; not paid` : `approved ${who} without a completion form; Billing told to pay`}${moved ? '; the job is ready to edit' : ''}. ${reason.slice(0, 300)}`
  }
}

/** booking_status for a lifecycle stage, as the current console's updateBookingStage sets it. */
export function bookingStatusForStage(stage) {
  if (stage === 'booking_complete') return 'completed'
  const inProgress = ['client_talent_communication_in_progress', 'talent_5_day_check_in', 'event_day', 'media_uploaded', 'media_issue', 'media_approved', 'talent_paid', 'media_delivered_partial', 'media_delivered_full']
  return inProgress.includes(stage) ? 'in_progress' : null
}
